import React, {useCallback, useEffect, useLayoutEffect, useState} from "react";
/** @jsxImportSource @emotion/react */
import {css} from "@emotion/react";
import {BROADCAST_ON_PASS_MOBILE_HEADER_TITLE, useApplicationContext} from "../../context/ApplicationContextProvider";
import {HEADER_TYPE_BASE} from "../_common/section/Header";
import {FOOTER_TYPE_NONE} from "../_common/section/Footer";
import SubjectViewer from "./component/SubjectViewer";
import SubjectList from "./component/SubjectList";
import {useMobileDetect, useMountEffect, useParameters, useSetDocumentTitle} from "../../utils/Hooks";
import {useModelContext} from "../../context/ModelContextProvider";
import {COURSE_INIT_SET} from "../../constant/options";
import {dialog_type_custom, useDialogContext} from "../../context/DialogContextProvider";
import {useDispatch} from "react-redux";
import CourseNoticeModal from "../_common/component/CourseNoticeModal";
import {storage_get, storage_set} from "../../common";
import {useNavigate} from "react-router-dom";
import {lang} from "../../lang";
import Log from "../../common/Log";

const ContainerSubject = () => {
    // ApplicationContext
    useApplicationContext({
        header: {
            type: HEADER_TYPE_BASE,
        },
        footer: {
            type: FOOTER_TYPE_NONE,
        },
    });

    const {broadcast} = useApplicationContext();
    const {is_mobile} = useMobileDetect();
    const {params} = useParameters();
    const {showDialog, alert} = useDialogContext();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const {isSuccess, isError, course_model} = useModelContext();

    // state
    const [course, setCourse] = useState(COURSE_INIT_SET);
    const [subject_set, setSubjectSet] = useState([]);
    const [course_notice, setCourseNotice] = useState(null);
    const [can_render, setCanRender] = useState(false);

    // css style
    const {css} = useComponentStyle(is_mobile);

    useSetDocumentTitle(course.title);

    useMountEffect(() => {
        updateUi();
    });

    const updateUi = async () => {
        setCanRender(false);

        const course_id = params.course_id;
        const course_detail_res = await course_model.getCourseDetail(course_id);

        if (isSuccess(course_detail_res)) {
            setCourse({
                id: course_detail_res.data.course.id,
                title: course_detail_res.data.course.title,
                document_url: course_detail_res.data.course.document_url,
            });
            setSubjectSet(course_detail_res.data.course.subject_set);

            if (course_detail_res.data.course.notice) {
                setCourseNotice(course_detail_res.data.course.notice);
            }
        } else if (isError(course_detail_res)) {
            Log.private("getCourseDetail 에러", course_detail_res);

            await alert(lang.request.request_error_in_course);

            setCourse(COURSE_INIT_SET);
            setSubjectSet([]);
            setCourseNotice(null);

            navigate("/");
        }

        setCanRender(true);
    };

    useLayoutEffect(() => {
        if (is_mobile && course) {
            broadcast.send(BROADCAST_ON_PASS_MOBILE_HEADER_TITLE, course.title);
        }
    }, [course, is_mobile, broadcast]);

    // 하루동안 notice 보지 않기
    const setLocalStorageNeverShowNotice = useCallback(() => {
        let exist_value = storage_get("never_show_notice_set");
        if (!exist_value) {
            storage_set("never_show_notice_set", [course_notice.id], 1000 * 60 * 60 * 24);
        } else {
            // 이미 로컬스토리지에 notice id를 가지고 있다면 notice를 띄우지 않기 때문에 두 번 클릭할 일은 없음
            if (exist_value.includes(course_notice.id)) {
                // 혹시 모를 경우를 대비하여 expire_time만 초기화
                storage_set("never_show_notice_set", exist_value, 1000 * 60 * 60 * 24);
            } else {
                // 로컬스토리지에 현재 notice id를 가지고 있지 않다면 추가
                exist_value.push(course_notice.id);
                storage_set("never_show_notice_set", exist_value, 1000 * 60 * 60 * 24);
            }
        }
    }, [course_notice]);

    useEffect(() => {
        if (course_notice) {
            let storage_saved_notice_id_set = storage_get("never_show_notice_set");
            if (!storage_saved_notice_id_set || !storage_saved_notice_id_set.includes(course_notice.id)) {
                showDialog({
                    type: dialog_type_custom,
                    component: CourseNoticeModal,
                    component_props: {
                        content_title: course_notice.title,
                        content: course_notice.content,
                        set_never_show: () => {
                            setLocalStorageNeverShowNotice();
                        },
                    },
                    with_history: false,
                });
            }
        }
    }, [course_notice, dispatch, showDialog, setLocalStorageNeverShowNotice]);

    return (
        can_render && (
            <div css={css.subject_container_css}>
                <div css={css.viewer_section_css}>
                    <SubjectViewer course={course} subject_set={subject_set} />
                </div>
                <div css={css.subject_section_css}>
                    <SubjectList course={course} subject_set={subject_set} />
                </div>
            </div>
        )
    );
};

export default ContainerSubject;

export const useComponentStyle = (is_mobile) => {
    return {
        css: {
            subject_container_css: css({
                position: "relative",
                width: "100%",
                maxWidth: "1128px",
                margin: "auto",
                height: "100vh",
                display: "flex",
                justifyContent: "center",
                "@media (max-width: 950px)": {
                    flexDirection: "column",
                    display: "block",
                    "& > div": {
                        marginLeft: 0,
                        width: "100%",
                        marginBottom: "2em",
                    },
                },
            }),
            viewer_section_css: css({
                marginLeft: "10px",
                width: "60%",
                minWidth: "260px",
                boxSizing: "border-box",
            }),
            subject_section_css: css({
                width: "40%",
                minWidth: "260px",
                boxSizing: "border-box",
                padding: is_mobile ? "0" : ".5em",
            }),
        },
    };
};
